<script>
  import { onMount, onDestroy } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import HierarchyPath from '../HierarchyPath/index.svelte';
  import StatusBadge from '../StatusBadge.svelte';

  let { propertyId, propertyName = '' } = $props();

  let searchTerm = $state('');
  let searchResults = $state([]);
  let isLoading = $state(false);
  let currentPage = $state(1);
  let hasMorePages = $state(false);
  let totalPages = $state(0);
  let searchTimeout;
  let searchContainer = $state();

  // Debounced search function
  function debounceSearch() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      performSearch(1); // Reset to page 1 for new search
    }, 300);
  }

  // Perform search API call
  async function performSearch(page = 1, append = false) {
    if (isLoading) return;

    isLoading = true;

    try {
      const response = await fetch(
        `/properties/${propertyId}/search?search_term=${encodeURIComponent(searchTerm)}&page=${page}`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      if (append) {
        searchResults = [...searchResults, ...data.results];
      } else {
        searchResults = data.results;
      }

      currentPage = data.pagy.page;
      totalPages = data.pagy.pages;
      hasMorePages = data.pagy.page < data.pagy.pages;

    } catch (error) {
      console.error('Search error:', error);
    } finally {
      isLoading = false;
    }
  }

  // Load more results for infinite scroll
  function loadMore() {
    if (hasMorePages && !isLoading) {
      performSearch(currentPage + 1, true);
    }
  }

  // Infinite scroll handler
  function handleScroll() {
    if (!searchContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = searchContainer;
    const threshold = 50; // Load more when 50px from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMore();
    }
  }

  // Clear search
  function clearSearch() {
    searchTerm = '';
    searchResults = [];
    currentPage = 1;
    hasMorePages = false;
    totalPages = 0;
  }

  // Format container type for display
  function formatContainerType(type) {
    if (!type) return 'Unknown';
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  // Handle navigation to item/container
  function navigateToItem(result, event = null) {
    if (event) {
      event.preventDefault();
    }

    if (result.type === 'container') {
      router.visit(`/containers/${result.id}`);
    } else {
      router.visit(`/properties/${propertyId}/items/${result.id}`);
    }
  }

  onMount(() => {
    // Perform initial search if there's a search term
    if (searchTerm.trim()) {
      performSearch(1);
    }
  });

  onDestroy(() => {
    clearTimeout(searchTimeout);
  });
</script>

<div class="relative">
  <!-- Search input: one prominent pill -->
  <label class="flex items-center gap-2.5 bg-white border border-gray-200 rounded-full px-5 py-2.5 shadow-sm focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-100 transition-all">
    <svg class="h-4.5 w-4.5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
    </svg>
    <input
      type="text"
      bind:value={searchTerm}
      oninput={debounceSearch}
      placeholder={`Search items and containers${propertyName ? ` in ${propertyName}` : ''}…`}
      class="flex-1 border-none bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
    />
    {#if searchTerm}
      <button
        onclick={clearSearch}
        aria-label="Clear search"
        class="flex-shrink-0 text-gray-400 hover:text-gray-600"
      >
        <svg class="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    {/if}
  </label>

  <!-- Results -->
  {#if searchTerm.trim()}
    <div class="mt-3 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {#if searchResults.length > 0}
        <div class="px-5 py-3 border-b border-gray-100 text-xs text-gray-500">
          Showing {searchResults.length} results for “{searchTerm}”
          {#if totalPages > 1}
            (page {currentPage} of {totalPages})
          {/if}
        </div>

        <!-- Results container with infinite scroll -->
        <div
          bind:this={searchContainer}
          onscroll={handleScroll}
          class="max-h-96 overflow-y-auto px-2 py-1.5"
        >
          <ul class="divide-y divide-gray-100">
            {#each searchResults as result}
              <li class="group relative flex items-center gap-3.5 px-3 py-2.5 rounded-xl hover:bg-gray-50">
                <!-- Invisible clickable overlay -->
                <button
                  class="absolute inset-0 w-full h-full cursor-pointer bg-transparent border-none z-10"
                  onclick={(e) => navigateToItem(result, e)}
                  title="View {result.type}"
                  aria-label="View {result.name}"
                ></button>

                <!-- Thumbnail -->
                {#if result.image_url}
                  <img src={result.image_url} alt={result.name} class="h-13 w-13 flex-shrink-0 object-cover rounded-lg bg-gray-100" />
                {:else}
                  <div class="h-13 w-13 flex-shrink-0 rounded-lg {result.type === 'container' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'} grid place-items-center">
                    {#if result.type === 'container'}
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/></svg>
                    {:else}
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                    {/if}
                  </div>
                {/if}

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-sm font-semibold text-gray-900 truncate">{result.name}</h3>
                    <span class="text-[11px] font-semibold rounded-full px-2 py-0.5 {result.type === 'container' ? 'bg-amber-100 text-amber-800' : 'bg-brand-100 text-brand-800'}">
                      {result.type === 'container' ? 'Container' : 'Item'}
                    </span>
                    <StatusBadge value={result.status} />
                  </div>
                  {#if result.description}
                    <p class="text-xs text-gray-500 truncate">{result.description}</p>
                  {/if}
                  <div class="mt-1">
                    <HierarchyPath path={result.hierarchy_path} highlightLast={false} />
                  </div>
                  <div class="flex flex-wrap items-center gap-2.5 mt-1 text-xs text-gray-400">
                    {#if result.type === 'container'}
                      <span>Type: {formatContainerType(result.container_type)}</span>
                      {#if result.item_count > 0}
                        <span>{result.item_count} items</span>
                      {/if}
                    {:else}
                      {#if result.remaining_quantity > 1}
                        <span>Qty: {result.remaining_quantity}</span>
                      {/if}
                    {/if}
                    {#if result.is_valuable}
                      <svg class="h-3.5 w-3.5 text-amber-500 inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Valuable"><title>Valuable</title><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
                    {/if}
                    {#if result.is_favorite}
                      <svg class="h-3.5 w-3.5 text-red-500 inline-block" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Favorite"><title>Favorite</title><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
                    {/if}
                  </div>
                </div>

                <!-- Edit action stays clickable above the overlay -->
                <a
                  href={result.type === 'container' ? `/containers/${result.id}/edit` : `/properties/${propertyId}/items/${result.id}/edit`}
                  onclick={(e) => e.stopPropagation()}
                  class="relative z-20 flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-brand-700 bg-white hover:bg-brand-50 border border-gray-200 hover:border-brand-600 rounded-lg shadow-sm transition-colors"
                  title="Edit {result.type}"
                >
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </a>
              </li>
            {/each}
          </ul>

          <!-- Loading more -->
          {#if isLoading && searchResults.length > 0}
            <div class="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
              <span class="h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
              Loading more results…
            </div>
          {/if}

          <!-- End of results -->
          {#if !hasMorePages && searchResults.length > 0 && !isLoading}
            <div class="text-center py-3 text-xs text-gray-400">End of results</div>
          {/if}
        </div>

      {:else if !isLoading}
        <div class="text-center py-8 px-5">
          <svg class="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <p class="text-sm text-gray-500">No results found for “{searchTerm}”</p>
          <p class="text-xs text-gray-400 mt-1">Try different keywords or check your spelling</p>
        </div>
      {/if}

      {#if isLoading && searchResults.length === 0}
        <div class="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
          <span class="h-4 w-4 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
          Searching…
        </div>
      {/if}
    </div>
  {/if}
</div>
